import type { CardHooks } from "./registry";
import { uid } from "@/lib/utils";

/**
 * Chain The Flow — Utility / Maneuver.
 *
 * Supabase row: `cardType: "Utility"`, `utilityType: "Maneuver"`,
 * `keywords: ["Chain"]`, ability:
 *   "Search your deck for an attack/defend card with chain. Reveal it,
 *    place it in hand and shuffle your deck."
 *
 * Implementation:
 *  - Filters deck candidates to ATTACK/DEFEND cards whose attack-keyword
 *    list OR defend-keyword list contains "chain" (case-insensitive). This
 *    matches Noah's specification: keywords on the attack/defend slots only,
 *    NOT the printed top-level `keywords` array.
 *  - Uses the generic `search-deck-by-filter` pending prompt. Engine resolver
 *    confirmSearchDeckByFilter handles add-to-hand and shuffleAfter.
 *  - shuffleAfter: true (standard tutor).
 *
 * Data-driven note: the filter is currently hard-coded to "chain on A/D
 * slots" because that's this card's design. Future tutors that want a
 * different filter should add their own hook (e.g. tutorByType, tutorByWeight)
 * and reuse the same `search-deck-by-filter` prompt with their own filtered
 * candidate list. The prompt itself is keyword-agnostic.
 */
export const chainTheFlow: CardHooks = {
  onUtilityPlay: (ctx) => {
    const { self, card, cardMap } = ctx;

    // Filter deck to A/D cards with "chain" on attack OR defend keyword slot.
    const candidates: { instanceId: string; cardId: string }[] = [];
    if (cardMap) {
      for (const cardId of self.deckCards) {
        const c = cardMap.get(cardId);
        if (!c) continue;
        if (c.cardType !== "Attack/Defend") continue;
        const attKws = (c.attackKeywords ?? []).map((k) => k.toLowerCase());
        const defKws = (c.defendKeywords ?? []).map((k) => k.toLowerCase());
        if (attKws.includes("chain") || defKws.includes("chain")) {
          // deckCards stores cardIds, not instance ids. Mint a stable id
          // here for UI selection state; confirmSearchDeckByFilter matches
          // by cardId to remove the first occurrence from the deck.
          candidates.push({ instanceId: uid("dk_"), cardId });
        }
      }
    }

    if (candidates.length === 0) {
      return {
        log: [
          `${card.name} — no Chain attack/defend cards in deck.`,
        ],
      };
    }

    return {
      prompt: {
        kind: "search-deck-by-filter" as const,
        playerNum: self.num as 1 | 2,
        filterLabel: `${card.name} — Search for an Attack/Defend with Chain`,
        sourceCardName: card.name,
        candidates,
        selectedInstanceIds: [],
        pickCount: 1,
        shuffleAfter: true,
      },
      log: [`${card.name} — searching deck for a Chain attack/defend…`],
    };
  },
};
